import React, { useState, useEffect, useRef, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { toast } from "react-toastify";
import Link from "next/link";
import Image from "next/image";
import { submitSampleRequest } from "@/api/FormsApi";
import { globalSearch } from "@/api/GlobalSearchApi";
import { getCDNImageUrl } from "@/utils/Common";
import styles from "./SampleRequestForm.module.scss";
import { usePathname } from "next/navigation";
import {
  trackFormLoaded,
  trackFormSubmitted,
  FORM_TYPES,
  getPageType,
} from "@/utils/gtmEvents";

// Validation schema
const validationSchema = Yup.object().shape({
  productName: Yup.string().trim().required("Product name is required"),
  quantity: Yup.string(),
  deliveryLocation: Yup.string().trim().required("Delivery location is required"),
  fullName: Yup.string().trim().required("Full name is required"),
  email: Yup.string()
    .matches(
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      "Enter a valid email address"
    )
    .required("Email is required"),
  companyName: Yup.string().trim().required("Company name is required"),
  mobileNumber: Yup.string(),
  recaptcha: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
    ? Yup.string().required("Please complete the reCAPTCHA")
    : Yup.string().nullable(),
  termsAccepted: Yup.boolean().oneOf(
    [true],
    "You must agree to the Terms & Conditions"
  ),
});

// Certification badges data (default variant)
const certifications = [
  { id: "iso", label: "ISO 9001", icon: "shield" },
  { id: "rspo", label: "RSPO Compliant", icon: "check" },
  { id: "gmp", label: "GMP Certified", icon: "certificate" },
  { id: "coa", label: "COA Included", icon: "file" },
];

// Feature list data (home variant)
const homeFeatures = [
  "Samples dispatched and delivered within 24–48 hours",
  "Quality-checked samples meeting defined standards and specifications",
  "Full visibility from request submission to final delivery",
  "Hands-on assistance for sample selection and coordination",
];

function SampleRequestForm({ variant = "default" }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const pathname = usePathname();

  // Autocomplete state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchTimeoutRef = useRef(null);

  // Track FORM_LOADED event on mount
  useEffect(() => {
    const pageUrl = typeof window !== "undefined" ? window.location.href : "";
    const pageType = getPageType(pathname);
    trackFormLoaded(FORM_TYPES.REQUEST_SAMPLE_QUOTES, pageUrl, pageType);
  }, [pathname]);

  // Debounced search function
  const handleProductSearch = useCallback(async (query) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await globalSearch(query);
        setSearchResults(results || []);
      } catch (error) {
        console.error("Search error:", error);
        setSearchResults([]);
      }
    }, 300);
  }, []);

  // Handle product selection from dropdown
  const handleSelectProduct = (product) => {
    formik.setFieldValue("productName", product.ChemicalName);
    setSearchResults([]);
    setIsSearching(false);
    setHighlightedIndex(-1);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isSearching || searchResults.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
          handleSelectProduct(searchResults[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsSearching(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Auto-reset success state after 5 seconds
  useEffect(() => {
    if (isSuccess) {
      const timeoutId = setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [isSuccess]);

  const formik = useFormik({
    initialValues: {
      productName: "",
      quantity: "",
      deliveryLocation: "",
      fullName: "",
      email: "",
      companyName: "",
      mobileNumber: "",
      recaptcha: null,
      termsAccepted: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true);

      try {
        // API expects a `products` array (multi-product). This form has a single
        // product row for now, so wrap it into the same payload shape as the modal.
        const { productName, quantity, ...rest } = values;
        const payload = {
          ...rest,
          products: [{ productName, quantity }],
        };
        const result = await submitSampleRequest(payload);

        if (result) {
          // Track FORM_SUBMITTED event
          const pageUrl = typeof window !== "undefined" ? window.location.href : "";
          const pageType = getPageType(pathname);
          trackFormSubmitted(FORM_TYPES.REQUEST_SAMPLE_QUOTES, pageUrl, pageType);

          setIsSuccess(true);
          formik.resetForm();
          window?.grecaptcha?.reset();
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handlePhoneFocus = () => setIsPhoneFocused(true);
  const handlePhoneBlur = () => {
    setIsPhoneFocused(false);
    formik.handleBlur("mobileNumber");
  };

  // Icon components
  const ShieldIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 12L11 14L15 10M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const CertificateIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M4 4H20V16H4V4Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 20L12 16L16 20" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 12C13.1046 12 14 11.1046 14 10C14 8.89543 13.1046 8 12 8C10.8954 8 10 8.89543 10 10C10 11.1046 10.8954 12 12 12Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const FileIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2V8H20" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 15L11 17L15 13" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Check circle broken icon for home variant
  const CheckCircleBrokenIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.66667 10L8.88889 12.2222L13.3333 7.77778M17.7778 10C17.7778 14.2955 14.2955 17.7778 10 17.7778C5.70446 17.7778 2.22222 14.2955 2.22222 10C2.22222 5.70446 5.70446 2.22222 10 2.22222C14.2955 2.22222 17.7778 5.70446 17.7778 10Z" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const getIcon = (iconType) => {
    switch (iconType) {
      case "shield": return <ShieldIcon />;
      case "check": return <CheckIcon />;
      case "certificate": return <CertificateIcon />;
      case "file": return <FileIcon />;
      default: return <ShieldIcon />;
    }
  };

  return (
    <div className={styles.sampleRequestFormSection}>
      <div className={styles.formWrapper}>
        {/* Background overlay */}
        <div className={styles.backgroundOverlay}></div>
        <div className={styles.radialGradientOverlay}></div>

        <div className={styles.formContent}>
          {/* Left Side - Heading and Badges/Features */}
          <div className={styles.leftSection}>
            <div className={styles.headingContainer}>
              {variant === "home" ? (
                <>
                  <h2>Fast-Track Sample Program</h2>
                  <p>
                    Get chemical product samples{" "}
                    <span>delivered within 24–48 hours</span> through a seamless,
                    tracked process. Verify quality and compliance before bulk
                    procurement and reduce risk.
                  </p>
                </>
              ) : (
                <>
                  <h2>
                    Quality You Can Trust,{" "}
                    <br />
                    Samples You Can <span>Rely On</span>
                  </h2>
                  <p>
                    All samples are securely packed, quality-verified, and dispatched from trusted partners. Every shipment includes complete documentation, certificates of analysis, and safety data sheets.
                  </p>
                </>
              )}
            </div>

            {variant === "home" ? (
              <>
                {/* Feature List */}
                <div className={styles.featureList}>
                  {homeFeatures.map((feature, index) => (
                    <div key={index} className={styles.featureItem}>
                      <CheckCircleBrokenIcon />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Explore Button */}
                <Link href="/request-a-sample" className={styles.exploreButton}>
                  Explore
                </Link>
              </>
            ) : (
              /* Certification Badges */
              <div className={styles.badgesContainer}>
                {certifications.map((cert) => (
                  <div key={cert.id} className={styles.badge}>
                    {getIcon(cert.icon)}
                    <span>{cert.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Side - Form */}
          <div className={styles.rightSection}>
            {isSuccess ? (
              <div className={styles.successContainer}>
                <div className={styles.successIcon}>
                  <Image
                    src={getCDNImageUrl("solidGreenContactUs.svg")}
                    alt="Success"
                    width={120}
                    height={120}
                  />
                </div>
                <p className={styles.successText}>
                  Your sample request has been submitted successfully! We will get back to you shortly.
                </p>
              </div>
            ) : (
            <form onSubmit={formik.handleSubmit}>
              {/* Sample Details Section */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Sample Details</label>

                <div className={styles.autocompleteContainer}>
                  <input
                    type="text"
                    name="productName"
                    placeholder="Product Name*"
                    value={formik.values.productName}
                    onChange={(e) => {
                      formik.handleChange(e);
                      handleProductSearch(e.target.value);
                      setIsSearching(true);
                      setHighlightedIndex(-1);
                    }}
                    onFocus={() => setIsSearching(true)}
                    onBlur={() => {
                      formik.handleBlur({ target: { name: "productName" } });
                      setTimeout(() => {
                        setIsSearching(false);
                        setHighlightedIndex(-1);
                      }, 200);
                    }}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                  />
                  {isSearching && searchResults.length > 0 && (
                    <div className={styles.autocompleteDropdown}>
                      {searchResults.map((item, index) => (
                        <div
                          key={item.id}
                          className={`${styles.autocompleteItem} ${
                            index === highlightedIndex ? styles.highlighted : ""
                          }`}
                          onMouseDown={() => handleSelectProduct(item)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          {item.ChemicalName}
                          {item.CasNumber ? `, ${item.CasNumber}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formik.touched.productName && formik.errors.productName && (
                  <div className={styles.errorMessage}>{formik.errors.productName}</div>
                )}

                <input
                  type="text"
                  name="quantity"
                  placeholder="Quantity"
                  value={formik.values.quantity}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.quantity && formik.errors.quantity && (
                  <div className={styles.errorMessage}>{formik.errors.quantity}</div>
                )}

                <input
                  type="text"
                  name="deliveryLocation"
                  placeholder="Delivery Location*"
                  value={formik.values.deliveryLocation}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.deliveryLocation && formik.errors.deliveryLocation && (
                  <div className={styles.errorMessage}>{formik.errors.deliveryLocation}</div>
                )}
              </div>

              {/* Contact Details Section */}
              <div className={styles.formSection}>
                <label className={styles.sectionLabel}>Contact Details</label>

                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name*"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.fullName && formik.errors.fullName && (
                  <div className={styles.errorMessage}>{formik.errors.fullName}</div>
                )}

                <input
                  type="email"
                  name="email"
                  placeholder="Email ID*"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className={styles.errorMessage}>{formik.errors.email}</div>
                )}

                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name*"
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.companyName && formik.errors.companyName && (
                  <div className={styles.errorMessage}>{formik.errors.companyName}</div>
                )}

                {/* Phone Input */}
                <div
                  className={`${styles.phoneInputContainer} ${
                    isPhoneFocused ? styles.phoneFocused : ""
                  }`}
                >
                  <PhoneInput
                    country={"us"}
                    enableSearch={true}
                    value={formik.values.mobileNumber}
                    onChange={(value) => {
                      formik.setFieldValue("mobileNumber", value);
                    }}
                    onFocus={handlePhoneFocus}
                    onBlur={handlePhoneBlur}
                    placeholder="Mobile Number"
                    containerStyle={{
                      width: "100%",
                      height: "44px",
                    }}
                    inputStyle={{
                      width: "100%",
                      height: "44px",
                      fontSize: "16px",
                      fontFamily: "inherit",
                      backgroundColor: "rgba(8, 16, 42, 0.6)",
                      color: "white",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "0 8px 8px 0",
                      borderLeft: "none",
                      paddingLeft: "68px",
                      outline: "none",
                    }}
                    buttonStyle={{
                      backgroundColor: "rgba(8, 16, 42, 0.6)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px 0 0 8px",
                      borderRight: "none",
                      width: "60px",
                      padding: "0 12px 0 8px",
                      outline: "none",
                    }}
                    dropdownStyle={{
                      backgroundColor: "rgba(8, 16, 42, 0.95)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "8px",
                      zIndex: 10,
                      maxHeight: "200px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                    }}
                    searchStyle={{
                      backgroundColor: "rgba(8, 16, 42, 0.8)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: "6px",
                      color: "white",
                      padding: "8px 12px",
                      margin: "8px",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                </div>
                {formik.touched.mobileNumber && formik.errors.mobileNumber && (
                  <div className={styles.errorMessage}>{formik.errors.mobileNumber}</div>
                )}
              </div>

              {/* Terms and reCAPTCHA */}
              <div className={styles.formFooter}>
                {/* Terms checkbox */}
                <div className={styles.termsContainer}>
                  <input
                    type="checkbox"
                    id="termsAccepted"
                    name="termsAccepted"
                    checked={formik.values.termsAccepted}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <label htmlFor="termsAccepted">
                    <span>By submitting, I agree with </span>
                    <Link href="/terms-and-conditions">
                      <span className={styles.termsLink}>Terms & Conditions</span>
                    </Link>
                  </label>
                </div>
                {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                  <div className={styles.errorMessage}>
                    {formik.errors.termsAccepted}
                  </div>
                )}

                {/* reCAPTCHA */}
                {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
                  <div className={styles.recaptchaContainer}>
                    <ReCAPTCHA
                      sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                      theme="dark"
                      onChange={(value) => {
                        formik.setFieldValue("recaptcha", value);
                      }}
                    />
                    {formik.touched.recaptcha && formik.errors.recaptcha && (
                      <div className={styles.recaptchaError}>
                        {formik.errors.recaptcha}
                      </div>
                    )}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SampleRequestForm;
